import {
  useHtzWorkOrderDeletePositionMutation,
  useHtzWorkOrderListPositionQuery,
  useHtzWorkOrderPutPositionMutation,
  useLazyHtzWorkOrderListPositionQuery,
} from "@/services/backend/htz/work-order/api/work-order-api";
import { useCallback, useState } from "react";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbDelete,
  documentDbGetAll,
  documentDbGetAllDeleted,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { Position } from "@/services/backend/htz/work-order/types/work-order";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  Query,
  useDocumentDBGetAll,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useWorkOrderPositionsOffline prepares two functions. One to
 * prepare the work order for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useWorkOrderPositionsOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzWorkOrderListPositionQuery();
  const [put] = useHtzWorkOrderPutPositionMutation();
  const [doDelete] = useHtzWorkOrderDeletePositionMutation();

  const prepMsg = t("Speichere Positionen ...");
  const syncMsg = t("Synchronisiere Positionen ...");
  const doneMsg = t("Positionen");

  const preparePositions = async () => {
    // Load the latest state
    const result = await load({
      workOrder: { active: true, values: [workOrderId] },
    });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("position");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await Promise.all(
      result.data!.items.map(async (position) => {
        await documentDbPut<Position>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "position",
          position,
        );
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncPositions = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbPositions = await documentDbGetAll<Position>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "position",
    );
    const deletedIdbPositions = await documentDbGetAllDeleted<Position>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "position",
    );

    // Sync to server
    // We first PUT all non-deleted or new entities back to the server
    await Promise.all(
      idbPositions.map(async (position) => {
        await put(position).unwrap();
      }),
    );
    // Then we delete the entities that where deleted offline.
    await Promise.all(
      deletedIdbPositions.map(async (position) => {
        await doDelete({
          id: position.id,
          workOrderId: position.workOrderId,
        });
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { preparePositions, syncPositions };
}

export function usePutPosition(
  workOrderId: string,
): Mutation<Position, Position> {
  const { offlineState } = useOfflineContext();

  const [putPositionMutation, { isLoading, error, isSuccess, reset }] =
    useHtzWorkOrderPutPositionMutation();

  const [
    putPositionIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<Position, Position>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "position",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [putPositionMutation, { isLoading, error, isSuccess, reset }];
    case OfflineState.OFFLINE:
      return [
        putPositionIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of position not possible is state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useListPosition(workOrderId: string): Query<Position[]> {
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzWorkOrderListPositionQuery({
    workOrder: {
      active: true,
      values: [workOrderId!],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<Position>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "position",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.items,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useDeletePosition(): Mutation<Position, null> {
  const { offlineState } = useOfflineContext();
  const { addStale } = useStaleContext();

  const [idbIsLoading, setIdbIsLoading] = useState(false);
  const [idbError, setIdbError] = useState<unknown>();
  const [idbIsSuccess, setIdbIsSuccess] = useState<boolean>(false);
  const idbReset = useCallback(() => {
    setIdbError(undefined);
    setIdbIsSuccess(false);
  }, [setIdbError]);

  const deletePositionIndexDB = useCallback(
    (position: Position) => {
      setIdbIsLoading(true);
      setIdbError(undefined);
      setIdbIsSuccess(false);

      // The result promise never rejects (errors are folded into `{ error }`),
      // so fire-and-forget callers do not produce an unhandled rejection. Only
      // `unwrap()` surfaces the error by throwing.
      const resultPromise = documentDbDelete(
        GSMDocumentKind.WorkOrder,
        position.workOrderId,
        "position",
        position.id,
      )
        .then((): { data: null } | { error: unknown } => {
          addStale("position");
          setIdbIsSuccess(true);
          return { data: null };
        })
        .catch((err): { data: null } | { error: unknown } => {
          setIdbError({ status: 500 });
          return { error: err };
        })
        .finally(() => {
          setIdbIsLoading(false);
        });

      return {
        unwrap: () =>
          resultPromise.then((result) => {
            if ("error" in result) {
              throw result.error;
            }
            return result.data;
          }),
      };
    },
    [addStale],
  );

  const [deletePositionMutation, { isLoading, error, isSuccess, reset }] =
    useHtzWorkOrderDeletePositionMutation();

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [deletePositionMutation, { isLoading, error, isSuccess, reset }];
    case OfflineState.OFFLINE:
      return [
        deletePositionIndexDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        (position: Position) => {
          throw new Error(
            `Delete of ${position} not possible is state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}
